/**
 * Settings modal with configuration options and debug info
 */

import React from 'react';
import { useSettings, FONT_OPTIONS } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDetentCache } from '../hooks/useDetentCache';
import styles from './SettingsModal.module.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDetentId: string | null;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, currentDetentId }) => {
  const { settings, updateSettings, resetSettings } = useSettings();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { getDetent } = useDetentCache();

  if (!isOpen) return null;

  const currentDetent = currentDetentId ? getDetent(currentDetentId) : null;
  const activeBlock = currentDetent?.blocks?.[0];

  // Get debug info from current detent
  const debugInfo = activeBlock?.meta || null;
  const filename = currentDetentId
    ? currentDetent?.isZany
      ? `zany/${currentDetentId}.json`
      : `years/${currentDetentId}.json`
    : 'N/A';

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Settings</h2>
          <div className={styles.headerActions}>
            <a
              href="https://turow.ski/cascades"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.aboutLinkHeader}
              aria-label="About CASCADES"
              title="About CASCADES"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-6h2v6zm0-8H9V5h2v2z"/>
              </svg>
            </a>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className={styles.content}>
          {/* Display Options */}
          <section className={styles.section}>
            <h3>Display</h3>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={isDarkMode}
                onChange={toggleDarkMode}
              />
              <span>Dark mode</span>
            </label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.alternatingLines}
                onChange={(e) => updateSettings({ alternatingLines: e.target.checked })}
              />
              <span>Alternating line colors</span>
            </label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.animationEnabled}
                onChange={(e) => updateSettings({ animationEnabled: e.target.checked })}
              />
              <span>Enable animations</span>
            </label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.autoscaleLines}
                onChange={(e) => updateSettings({ autoscaleLines: e.target.checked })}
              />
              <span>Autoscale to single-line height</span>
            </label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.horizontalScroll}
                onChange={(e) => updateSettings({ horizontalScroll: e.target.checked })}
              />
              <span>Enable horizontal scrolling</span>
            </label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.portraitMode}
                onChange={(e) => updateSettings({ portraitMode: e.target.checked })}
              />
              <span>Portrait mode (rotate 90°)</span>
            </label>
            <div className={styles.fontSizeControl}>
              <span className={styles.fontSizeLabel}>
                Max lines: {settings.maxLines === 0 ? 'All (25)' : settings.maxLines}
              </span>
              <div className={styles.fontSizeButtons}>
                <button
                  className={styles.fontSizeBtn}
                  onClick={() => updateSettings({ maxLines: Math.max(0, settings.maxLines - 1) })}
                  disabled={settings.maxLines === 0}
                >
                  −
                </button>
                <input
                  type="range"
                  className={styles.fontSizeSlider}
                  min="0"
                  max="25"
                  value={settings.maxLines}
                  onChange={(e) => updateSettings({ maxLines: parseInt(e.target.value) })}
                />
                <button
                  className={styles.fontSizeBtn}
                  onClick={() => updateSettings({ maxLines: Math.min(25, settings.maxLines + 1) })}
                  disabled={settings.maxLines === 25}
                >
                  +
                </button>
              </div>
            </div>
          </section>

          {/* Typography */}
          <section className={styles.section}>
            <h3>Typography</h3>
            <label className={styles.selectLabel}>
              <span>Font Family:</span>
              <select
                className={styles.fontSelect}
                value={settings.fontFamily}
                onChange={(e) => updateSettings({ fontFamily: e.target.value })}
              >
                {FONT_OPTIONS.map((font) => (
                  <option key={font.value} value={font.value}>
                    {font.label}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.fontSizeControl}>
              <span className={styles.fontSizeLabel}>Font Size: {settings.fontSize}px</span>
              <div className={styles.fontSizeButtons}>
                <button
                  className={styles.fontSizeBtn}
                  onClick={() => updateSettings({ fontSize: Math.max(4, settings.fontSize - 1) })}
                  aria-label="Decrease font size"
                >
                  −
                </button>
                <input
                  type="range"
                  min="4"
                  max="24"
                  value={settings.fontSize}
                  onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) })}
                  className={styles.fontSizeSlider}
                />
                <button
                  className={styles.fontSizeBtn}
                  onClick={() => updateSettings({ fontSize: Math.min(24, settings.fontSize + 1) })}
                  aria-label="Increase font size"
                >
                  +
                </button>
              </div>
            </div>
          </section>

          {/* Color Customization */}
          <section className={styles.section}>
            <h3>Colors</h3>
            <div className={styles.colorPickers}>
              <label className={styles.colorLabel}>
                <span>Odd lines:</span>
                <input
                  type="color"
                  value={settings.customColors.lineOdd || '#000000'}
                  onChange={(e) => updateSettings({
                    customColors: { ...settings.customColors, lineOdd: e.target.value }
                  })}
                />
              </label>
              <label className={styles.colorLabel}>
                <span>Even lines:</span>
                <input
                  type="color"
                  value={settings.customColors.lineEven || '#333333'}
                  onChange={(e) => updateSettings({
                    customColors: { ...settings.customColors, lineEven: e.target.value }
                  })}
                />
              </label>
              <label className={styles.colorLabel}>
                <span>Background:</span>
                <input
                  type="color"
                  value={settings.customColors.background || '#ffffff'}
                  onChange={(e) => updateSettings({
                    customColors: { ...settings.customColors, background: e.target.value }
                  })}
                />
              </label>
              <label className={styles.colorLabel}>
                <span>Text:</span>
                <input
                  type="color"
                  value={settings.customColors.text || '#000000'}
                  onChange={(e) => updateSettings({
                    customColors: { ...settings.customColors, text: e.target.value }
                  })}
                />
              </label>
            </div>
          </section>

          {/* Model Filter */}
          <section className={styles.section}>
            <h3>Model Filter</h3>
            <label className={styles.selectLabel}>
              <span>Show detents from:</span>
              <select
                className={styles.fontSelect}
                value={settings.modelFilter}
                onChange={(e) => updateSettings({ modelFilter: e.target.value })}
              >
                <option value="">All models</option>
                <option value="anthropic:claude-opus-4-5-20251101">Claude Opus 4.5</option>
                <option value="openai:gpt-4.1">GPT-4.1</option>
                <option value="openai:gpt-4o">GPT-4o</option>
              </select>
            </label>
            <p className={styles.filterNote}>
              {settings.modelFilter
                ? `Filtering to ${settings.modelFilter.split(':')[1] || settings.modelFilter}`
                : 'Showing all available detents'}
            </p>
          </section>

          {/* Debug Information */}
          <section className={styles.section}>
            <h3>Current Detent Info</h3>
            <div className={styles.debugInfo}>
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>File:</span>
                <span className={styles.debugValue}>{filename}</span>
              </div>
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>Model:</span>
                <span className={styles.debugValue}>
                  {debugInfo?.model as string || 'N/A'}
                </span>
              </div>
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>Created:</span>
                <span className={styles.debugValue}>
                  {(debugInfo?.created_at || debugInfo?.generated_at)
                    ? new Date((debugInfo.created_at || debugInfo.generated_at) as string).toLocaleString()
                    : 'N/A'}
                </span>
              </div>
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>Version:</span>
                <span className={styles.debugValue}>
                  {debugInfo?.version as string || 'N/A'}
                </span>
              </div>
            </div>
          </section>

          {/* Reset Button */}
          <div className={styles.actions}>
            <button className={styles.resetButton} onClick={resetSettings}>
              Reset to Defaults
            </button>
          </div>

        </div>
      </div>
    </>
  );
};
